import React from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import {
  TrendingUp,
  Globe,
  Zap,
  ChevronRight,
  BarChart3,
} from 'lucide-react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  ComposedChart,
  Line,
} from 'recharts';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Badge } from '../ui/badge';
import { TabsContent } from '../ui/tabs';
import { formatNumber } from './constants';

const OverviewTab = ({ analytics, topPosts, platformHealth, pieData }) => (
  <TabsContent value="overview" className="space-y-6">
    <div className="grid lg:grid-cols-2 gap-6">
      {/* Performance Trend Chart */}
      <Card className="border-slate-100">
        <CardHeader>
          <CardTitle className="text-lg font-semibold flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-emerald-500" />
            Performance Trend
          </CardTitle>
          <CardDescription>Reach and engagement over time</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={analytics?.trend_data || []}>
                <defs>
                  <linearGradient id="colorReach" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10B981" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#10B981" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis
                  dataKey="date"
                  stroke="#94a3b8"
                  fontSize={11}
                  tickFormatter={(value) => format(new Date(value), 'MMM d')}
                />
                <YAxis stroke="#94a3b8" fontSize={11} yAxisId="left" />
                <YAxis stroke="#94a3b8" fontSize={11} yAxisId="right" orientation="right" />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'white',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    fontSize: '12px',
                  }}
                  labelFormatter={(value) => format(new Date(value), 'MMM d, yyyy')}
                />
                <Legend />
                <Area
                  type="monotone"
                  dataKey="reach"
                  stroke="#10B981"
                  fillOpacity={1}
                  fill="url(#colorReach)"
                  name="Reach"
                  yAxisId="left"
                />
                <Line
                  type="monotone"
                  dataKey="engagement"
                  stroke="#8B5CF6"
                  strokeWidth={2}
                  dot={false}
                  name="Engagement"
                  yAxisId="right"
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Platform Distribution Pie Chart */}
      <Card className="border-slate-100">
        <CardHeader>
          <CardTitle className="text-lg font-semibold flex items-center gap-2">
            <Globe className="h-5 w-5 text-blue-500" />
            Reach by Platform
          </CardTitle>
          <CardDescription>Distribution across social networks</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-[300px]">
            {pieData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {pieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(value) => formatNumber(value)}
                    contentStyle={{
                      backgroundColor: 'white',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                    }}
                  />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-full text-slate-500">
                No platform data available
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>

    {/* Top Performing Posts */}
    <Card className="border-slate-100">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <Zap className="h-5 w-5 text-amber-500" />
              Top Performing Posts
            </CardTitle>
            <CardDescription>Your best content by engagement</CardDescription>
          </div>
          <Link to="/posts?status=published">
            <Button variant="ghost" size="sm">
              View All <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </Link>
        </div>
      </CardHeader>
      <CardContent>
        {topPosts.length > 0 ? (
          <div className="space-y-4">
            {topPosts.slice(0, 5).map((post, i) => (
              <div
                key={post.post_id}
                className="flex items-center gap-4 p-3 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors"
              >
                <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-600 font-bold text-sm">
                  #{i + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <Link to={`/posts/${post.post_id}`}>
                    <p className="text-sm text-slate-700 truncate hover:text-emerald-600 transition-colors">
                      {post.content}
                    </p>
                  </Link>
                  <div className="flex items-center gap-2 mt-1">
                    {post.platforms.map((p) => (
                      <Badge key={p} variant="secondary" className="text-xs px-1.5 py-0">
                        {p}
                      </Badge>
                    ))}
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-lg font-bold text-slate-900">{formatNumber(post.analytics.engagement)}</p>
                  <p className="text-xs text-slate-500">engagement</p>
                </div>
                <div className="text-right">
                  <Badge className="bg-emerald-100 text-emerald-700">
                    {post.analytics.engagement_rate}%
                  </Badge>
                  <p className="text-xs text-slate-500 mt-1">eng. rate</p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-slate-500">
            <BarChart3 className="h-12 w-12 text-slate-300 mx-auto mb-3" />
            <p>No published posts yet</p>
            <Link to="/posts/new">
              <Button className="mt-4 bg-emerald-500 hover:bg-emerald-600">Create Post</Button>
            </Link>
          </div>
        )}
      </CardContent>
    </Card>

    {/* Connected Platforms */}
    {platformHealth && (
      <Card className="border-slate-100">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg font-semibold flex items-center gap-2">
                <Globe className="h-5 w-5 text-blue-500" />
                Connected Platforms
              </CardTitle>
              <CardDescription>
                {platformHealth.summary.connected} connected &middot; {platformHealth.summary.healthy} live &middot; Score: {platformHealth.summary.overall_score}%
              </CardDescription>
            </div>
            <Link to="/calendars">
              <Button variant="ghost" size="sm">
                Manage <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </Link>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {Object.entries(platformHealth.platforms)
              .filter(([, p]) => p.connected)
              .map(([name, p]) => (
                <div key={name} className="flex items-center gap-2 p-2 rounded-lg bg-slate-50">
                  <div
                    className={`w-2 h-2 rounded-full ${
                      p.health_status === 'healthy'
                        ? 'bg-emerald-500'
                        : p.health_status === 'demo'
                        ? 'bg-amber-400'
                        : p.health_status === 'stale'
                        ? 'bg-red-400'
                        : 'bg-slate-300'
                    }`}
                  />
                  <span className="text-sm font-medium text-slate-700 capitalize">
                    {name.replace('_', ' ')}
                  </span>
                  <Badge variant="secondary" className="ml-auto text-[10px] px-1.5 py-0">
                    {p.data_source === 'api' ? 'Live' : p.data_source === 'mock' ? 'Demo' : 'N/A'}
                  </Badge>
                </div>
              ))}
            {platformHealth.summary.connected === 0 && (
              <div className="col-span-full text-center py-4 text-slate-500 text-sm">
                No platforms connected yet.{' '}
                <Link to="/calendars" className="text-emerald-600 hover:underline">
                  Connect accounts
                </Link>{' '}
                to get real analytics.
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    )}
  </TabsContent>
);

export default OverviewTab;
